import schema from './portalThemeSchema.v1.json';

// #189 (ra#499 S3): the shop's colours and fonts as return-api serves them
// (`custom_theme` of GET /settings/portal, or the preview fragment). ra sends
// canonical values only; the portal re-validates them with strict canonical
// patterns against the FIXED token list of its fixture, because they end up in
// a stylesheet. A malformed layer is ignored as a whole.

export interface ShopTheme {
  // sparse: an absent token keeps its version2 default
  tokens: Record<string, string>;
  // the families ra resolved from the effective font tokens, display first
  fonts: string[];
}

interface SchemaToken {
  type: string;
  default: string;
}

export const SCHEMA_TOKENS = schema.tokens as Record<string, SchemaToken>;
export const TOKEN_NAMES = Object.keys(SCHEMA_TOKENS);

const FAMILY = /^[A-Za-z0-9 ]{1,64}$/;
const COLOR = /^#[0-9a-f]{6}$/;
const RADIUS = /^(\d{1,2})px$/;
const LENGTH = '(?:0|-?\\d{1,2}(?:\\.\\d)?px)';
const SHADOW_COLOR =
  '(?:#[0-9a-f]{6}|rgba\\(\\d{1,3}, \\d{1,3}, \\d{1,3}, (?:0|1|0\\.\\d{1,3})\\))';
const SHADOW_LAYER = `${LENGTH} ${LENGTH} ${LENGTH}(?: ${LENGTH})? ${SHADOW_COLOR}`;
const SHADOW = new RegExp(`^(?:none|${SHADOW_LAYER}(?:, ${SHADOW_LAYER})?)$`);
const FONT_ENTRY =
  "(?:'[A-Za-z0-9 ]{1,64}'|sans-serif|serif|monospace|system-ui|ui-sans-serif|ui-serif|cursive)";
const FONT_STACK = new RegExp(`^${FONT_ENTRY}(?:, ${FONT_ENTRY}){0,3}$`);

const validValue = (type: string, value: unknown): value is string => {
  if (typeof value !== 'string') return false;
  switch (type) {
    case 'color':
      return COLOR.test(value);
    case 'radius': {
      const radius = RADIUS.exec(value);
      return radius !== null && Number(radius[1]) <= 24;
    }
    case 'shadow':
      return SHADOW.test(value);
    case 'font':
      return FONT_STACK.test(value);
    default:
      return false;
  }
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  value !== null &&
  typeof value === 'object' &&
  Object.getPrototypeOf(value) === Object.prototype;

const familyOf = (font: unknown): string | null => {
  if (!isPlainObject(font) || typeof font.family !== 'string') return null;
  return FAMILY.test(font.family) ? font.family : null;
};

export const validateShopTheme = (value: unknown): ShopTheme | null => {
  if (!isPlainObject(value) || !isPlainObject(value.tokens)) return null;
  const tokens: Record<string, string> = {};
  for (const name of TOKEN_NAMES) {
    if (!Object.prototype.hasOwnProperty.call(value.tokens, name)) continue;
    const candidate = value.tokens[name];
    if (!validValue(SCHEMA_TOKENS[name].type, candidate)) return null;
    tokens[name] = candidate;
  }
  // a name outside the fixture (a later schema) is never a CSS property here
  if (Object.keys(tokens).length === 0) return null;
  if (!Array.isArray(value.fonts)) return null;
  const fonts: string[] = [];
  for (const font of value.fonts) {
    const family = familyOf(font);
    if (family === null) return null;
    if (!fonts.includes(family)) fonts.push(family);
  }
  return { tokens, fonts };
};

// the allowlisted families of the effective font tokens, display first: what the
// descriptor `fonts` must list for the layer to render as previewed
export const effectiveFamilies = (tokens: Record<string, string>): string[] => {
  const families: string[] = [];
  for (const name of ['--xc-font-display', '--xc-font-body']) {
    const stack = tokens[name] ?? SCHEMA_TOKENS[name].default;
    Array.from(stack.matchAll(/'([^']+)'/g), match => match[1]).forEach(
      family => {
        if (!families.includes(family)) families.push(family);
      }
    );
  }
  return families;
};

// the form kept in the cache and the preview fragment: what ra sends
export const toWireTheme = (theme: ShopTheme) => ({
  tokens: theme.tokens,
  fonts: theme.fonts.map(family => ({ family })),
});
